import React, { useEffect, useState } from "react";
import { Download, Smartphone, CheckCircle2, ShieldCheck, Zap, MessageCircle, ArrowRight, Sparkles } from "lucide-react";

const FEATURES = [
  { icon: Zap, title: "Fast Launch", desc: "Instant open" },
  { icon: ShieldCheck, title: "Auto Login", desc: "Session save" },
  { icon: MessageCircle, title: "Live Chat", desc: "24/7 support" },
];

export default function DownloadPage() {
  const [ripple, setRipple] = useState({ android: 0, ios: 0 });
  useEffect(() => {
    const id = setInterval(() => setRipple(r => ({ android: (r.android + 1) % 100, ios: (r.ios + 1) % 100 })), 30);
    return () => clearInterval(id);
  }, []);
  // Persist incoming ?ref= so Register page can auto-fill it after APK install → signup
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const ref = (p.get("ref") || "").trim().toUpperCase();
      if (ref) localStorage.setItem("rdyy_ref_code", ref);
    } catch (_e) { /* ignore */ }
  }, []);
  return (
    <div className="min-h-screen relative overflow-hidden"
      style={{ background: "radial-gradient(ellipse 90% 60% at 50% 0%, #1e40af 0%, #0c1e5e 45%, #060f36 100%)" }}
      data-testid="download-page">

      {/* Ambient orbs */}
      <div className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full opacity-40 blur-3xl animate-pulse"
        style={{ background: "radial-gradient(circle, rgba(251,191,36,0.55), transparent 60%)", animationDuration: "4s" }} />
      <div className="pointer-events-none absolute bottom-0 right-0 w-96 h-96 rounded-full opacity-25 blur-3xl"
        style={{ background: "radial-gradient(circle, #7c3aed, transparent 60%)" }} />

      {/* Floating sparkles */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {Array.from({ length: 12 }).map((_, i) => (
          <div key={i} className="absolute w-1 h-1 rounded-full bg-yellow-300 animate-float-sparkle"
            style={{
              left: `${(i * 13) % 100}%`,
              top: `${(i * 17) % 100}%`,
              animationDelay: `${i * 0.6}s`,
              animationDuration: `${5 + (i % 4)}s`,
            }} />
        ))}
      </div>

      <div className="relative z-10 max-w-md mx-auto px-6 pt-8 pb-16">
        {/* Brand header */}
        <div className="text-center mb-6">
          <div className="relative inline-block mb-4">
            <div className="absolute inset-0 rounded-3xl blur-3xl opacity-80"
              style={{ background: "radial-gradient(circle, #fbbf24 0%, transparent 65%)" }} />
            <img src="/brand/reddy-club-logo.png?v=2" alt="Reddy Club"
              className="relative w-36 h-36 rounded-3xl ring-4 ring-yellow-400/70 shadow-[0_0_60px_rgba(251,191,36,0.6)] object-cover animate-logo-float"
              data-testid="hero-logo" />
          </div>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-yellow-400/50 bg-blue-900/60 backdrop-blur-sm">
            <Sparkles className="w-3.5 h-3.5 text-yellow-300 animate-pulse" />
            <span className="text-[11px] font-black text-yellow-200 tracking-[0.2em]">OFFICIAL APP</span>
            <Sparkles className="w-3.5 h-3.5 text-yellow-300 animate-pulse" />
          </div>
          <h1 className="mt-3 font-black text-4xl tracking-tight text-gold-static">REDDY CLUB</h1>
          <div className="text-blue-100 text-sm mt-1">India's #1 Trusted Matka Platform</div>
        </div>

        {/* CTA — 2 buttons side by side */}
        <div className="grid grid-cols-1 gap-3 mb-6">
          {/* ANDROID */}
          <a
            href="/rdyy.apk"
            download="Rdyy.apk"
            data-testid="download-android-btn"
            className="group relative block overflow-hidden rounded-2xl p-[2px] active:scale-[0.98] transition-transform"
            style={{ background: "linear-gradient(120deg, #fde047 0%, #f59e0b 50%, #fde047 100%)", backgroundSize: "200% 100%", animation: "shine-slide 3s linear infinite" }}
          >
            <div className="relative flex items-center gap-4 bg-blue-950/85 backdrop-blur-sm rounded-2xl px-5 py-4">
              <div className="relative shrink-0">
                <div className="absolute inset-0 rounded-full bg-emerald-500/30 blur-xl animate-pulse" />
                <div className="relative w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-400 to-green-600 flex items-center justify-center shadow-lg">
                  <AndroidIcon className="w-8 h-8 text-white" />
                </div>
              </div>
              <div className="flex-1 min-w-0 text-left">
                <div className="text-[10px] font-black tracking-widest text-yellow-300 uppercase">Android User</div>
                <div className="font-display font-black text-xl text-white leading-tight">Download APK</div>
                <div className="text-[11px] text-blue-200 mt-0.5">~19 MB · v1.0 · Instant install</div>
              </div>
              <div className="w-11 h-11 rounded-full bg-gold-gradient text-blue-950 flex items-center justify-center shrink-0 group-hover:translate-x-1 transition-transform animate-bounce-x">
                <Download className="w-5 h-5" strokeWidth={3} />
              </div>
              {/* Ripple ring */}
              <div className="pointer-events-none absolute inset-0 rounded-2xl border-2 border-emerald-400/50 opacity-0 group-hover:opacity-100 animate-ping" />
            </div>
          </a>

          {/* iOS */}
          <a
            href="/"
            data-testid="download-ios-btn"
            className="group relative block overflow-hidden rounded-2xl p-[2px] active:scale-[0.98] transition-transform"
            style={{ background: "linear-gradient(120deg, #cbd5e1 0%, #94a3b8 50%, #cbd5e1 100%)", backgroundSize: "200% 100%", animation: "shine-slide 3s linear infinite" }}
          >
            <div className="relative flex items-center gap-4 bg-blue-950/85 backdrop-blur-sm rounded-2xl px-5 py-4">
              <div className="relative shrink-0">
                <div className="absolute inset-0 rounded-full bg-sky-400/30 blur-xl animate-pulse" />
                <div className="relative w-14 h-14 rounded-2xl bg-gradient-to-br from-slate-300 to-slate-500 flex items-center justify-center shadow-lg">
                  <AppleIcon className="w-8 h-8 text-white" />
                </div>
              </div>
              <div className="flex-1 min-w-0 text-left">
                <div className="text-[10px] font-black tracking-widest text-sky-300 uppercase">iPhone User</div>
                <div className="font-display font-black text-xl text-white leading-tight">Open Web App</div>
                <div className="text-[11px] text-blue-200 mt-0.5">Safari me open · Add to Home Screen</div>
              </div>
              <div className="w-11 h-11 rounded-full bg-slate-100 text-white flex items-center justify-center shrink-0 group-hover:translate-x-1 transition-transform">
                <ArrowRight className="w-5 h-5" strokeWidth={3} />
              </div>
              <div className="pointer-events-none absolute inset-0 rounded-2xl border-2 border-sky-400/50 opacity-0 group-hover:opacity-100 animate-ping" />
            </div>
          </a>
        </div>

        {/* Feature strip */}
        <div className="grid grid-cols-3 gap-2 mb-6">
          {FEATURES.map((f, i) => (
            <div key={i} className="bg-blue-950/60 border border-yellow-400/25 rounded-xl p-3 backdrop-blur-sm text-center">
              <div className="w-8 h-8 mx-auto rounded-lg bg-gold-gradient text-blue-950 flex items-center justify-center mb-1.5">
                <f.icon className="w-4 h-4" strokeWidth={2.5} />
              </div>
              <div className="text-[11px] font-black text-yellow-300">{f.title}</div>
              <div className="text-[9px] text-blue-200 mt-0.5">{f.desc}</div>
            </div>
          ))}
        </div>

        {/* Install steps */}
        <div className="rounded-2xl p-4 border border-yellow-400/25 bg-blue-950/60 backdrop-blur-md" data-testid="install-steps">
          <div className="text-yellow-300 font-black text-sm tracking-widest mb-3 uppercase flex items-center gap-2">
            <Smartphone className="w-4 h-4" /> Install Steps
          </div>
          <ol className="space-y-3 text-sm text-blue-50">
            <Step n="1" title="Download button dabao" desc="Android — APK save · iPhone — Safari me open" />
            <Step n="2" title="Unknown Sources ON" desc="(Android only) Settings → Security → Chrome → ALLOW" />
            <Step n="3" title="Install / Add to Home" desc="Android — INSTALL · iPhone — Share → Add to Home Screen" />
            <Step n="4" title="Login karo" desc="Mobile number + password se aa jao — session save rahega" />
          </ol>
        </div>

        {/* Trust note */}
        <div className="mt-6 flex items-center gap-2 justify-center text-emerald-300 text-[11px]">
          <CheckCircle2 className="w-4 h-4" />
          <span>Signed · No ads · Zero malware · Free forever</span>
        </div>
        <div className="mt-4 text-center">
          <a href="/" className="text-yellow-300 text-xs font-semibold underline underline-offset-4">← Website pe wapas jao</a>
        </div>
      </div>

      {/* Inline keyframes */}
      <style>{`
        @keyframes shine-slide { 0% { background-position: 0% 50%; } 100% { background-position: 200% 50%; } }
        @keyframes logo-float { 0%, 100% { transform: translateY(0) rotate(0); } 50% { transform: translateY(-6px) rotate(1deg); } }
        .animate-logo-float { animation: logo-float 4s ease-in-out infinite; }
        @keyframes bounce-x { 0%, 100% { transform: translateX(0); } 50% { transform: translateX(3px); } }
        .animate-bounce-x { animation: bounce-x 1.4s ease-in-out infinite; }
        @keyframes float-sparkle { 0% { transform: translateY(0) scale(0); opacity: 0; } 20% { opacity: 1; transform: scale(1); } 100% { transform: translateY(-80px) scale(0.3); opacity: 0; } }
        .animate-float-sparkle { animation: float-sparkle 5s ease-in-out infinite; }
      `}</style>
    </div>
  );
}

function Step({ n, title, desc }) {
  return (
    <li className="flex items-start gap-3">
      <div className="shrink-0 w-7 h-7 rounded-full bg-gold-gradient text-blue-950 font-black text-sm flex items-center justify-center shadow">{n}</div>
      <div>
        <div className="font-bold text-yellow-100">{title}</div>
        <div className="text-[12px] text-blue-200 leading-snug">{desc}</div>
      </div>
    </li>
  );
}

function AndroidIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M16.61 15.15c-.46 0-.84-.37-.84-.83s.38-.83.84-.83c.46 0 .83.37.83.83s-.37.83-.83.83m-9.22 0c-.46 0-.83-.37-.83-.83s.37-.83.83-.83.83.37.83.83-.37.83-.83.83m9.52-5.02l1.66-2.88c.09-.16.03-.36-.13-.45-.16-.09-.36-.03-.45.13l-1.68 2.92C15 9.15 13.55 8.75 12 8.75s-3 .4-4.31 1.10L6.01 6.93c-.09-.16-.29-.22-.45-.13-.16.09-.22.29-.13.45l1.66 2.88C4.27 11.71 2.66 14.35 2.5 17.5h19c-.16-3.15-1.77-5.79-4.59-7.37" />
    </svg>
  );
}

function AppleIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
  );
}
